import React from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, ChevronRight } from "lucide-react";
import { useCms } from "@/lib/cms";
import { useSiteSettings, waHref } from "@/lib/site";

export function WhatsAppIcon({ size = 28, className = "" }) {
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} className={className} fill="currentColor" aria-hidden="true">
      <path d="M16.003 3C8.832 3 3 8.83 3 15.997c0 2.293.6 4.533 1.74 6.507L3 29l6.66-1.71a12.96 12.96 0 0 0 6.34 1.65h.003C23.17 28.94 29 23.11 29 15.943 29 8.83 23.17 3 16.003 3Zm0 23.74h-.002a10.8 10.8 0 0 1-5.5-1.5l-.395-.234-3.953 1.015 1.055-3.85-.257-.395a10.73 10.73 0 0 1-1.65-5.78c0-5.95 4.84-10.79 10.79-10.79 5.94 0 10.77 4.84 10.77 10.735 0 5.95-4.84 10.8-10.86 10.8Zm5.92-8.08c-.325-.162-1.92-.947-2.218-1.055-.297-.108-.513-.162-.73.163-.216.324-.837 1.054-1.026 1.27-.19.217-.378.244-.703.082-.324-.163-1.37-.505-2.61-1.61-.965-.86-1.616-1.922-1.805-2.246-.19-.325-.02-.5.142-.662.146-.145.325-.379.487-.568.163-.19.217-.325.325-.541.108-.217.054-.406-.027-.568-.081-.163-.73-1.758-1-2.407-.263-.632-.53-.546-.73-.556l-.622-.011a1.19 1.19 0 0 0-.866.406c-.297.325-1.135 1.11-1.135 2.705 0 1.596 1.162 3.138 1.324 3.354.163.217 2.287 3.49 5.54 4.894.774.334 1.378.534 1.85.683.777.247 1.484.212 2.043.129.623-.093 1.92-.785 2.19-1.543.271-.758.271-1.407.19-1.543-.08-.135-.297-.216-.622-.379Z"/>
    </svg>
  );
}

/** Floating WhatsApp button with ready-made messages (Admin → Content → WhatsApp button). */
export function WhatsAppWidget() {
  const c = useCms("whatsapp");
  const site = useSiteSettings();
  const { pathname } = useLocation();
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState("");
  const [nudge, setNudge] = React.useState(false);

  React.useEffect(() => { setOpen(false); }, [pathname]);
  React.useEffect(() => {
    // a small "Need help?" bubble once per visit, after a few seconds
    let seen = false;
    try { seen = sessionStorage.getItem("wa-nudge") === "1"; } catch { /* ignore */ }
    if (seen) return;
    const t = setTimeout(() => { setNudge(true); try { sessionStorage.setItem("wa-nudge", "1"); } catch { /* ignore */ } }, 6000);
    const h = setTimeout(() => setNudge(false), 14000);
    return () => { clearTimeout(t); clearTimeout(h); };
  }, []);

  const phone = (c.phone || "").trim() || site.whatsapp;
  if (c.enabled === false || !phone) return null;
  const messages = (c.messages || []).filter((m) => m && (m.text || m.label));
  const left = c.side === "left";
  const onProduct = pathname.startsWith("/product/");
  const withPage = (msg) => (onProduct && typeof window !== "undefined" ? `${msg}\n${window.location.href}` : msg);
  const send = (msg) => {
    window.open(waHref(phone, withPage(msg)), "_blank", "noopener");
    setOpen(false);
  };

  return (
    <div className={`fixed z-[60] bottom-4 sm:bottom-6 ${left ? "left-4 sm:left-6" : "right-4 sm:right-6"} flex flex-col ${left ? "items-start" : "items-end"}`} data-testid="whatsapp-widget">
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 16, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16, scale: 0.96 }} transition={{ duration: 0.2 }}
                      className={`mb-3 w-[calc(100vw-2rem)] max-w-[360px] rounded-3xl overflow-hidden bg-white shadow-[0_30px_70px_-20px_rgba(0,0,0,.45)] border border-neutral-100 ${left ? "origin-bottom-left" : "origin-bottom-right"}`}
                      data-testid="whatsapp-panel" role="dialog" aria-label="Chat on WhatsApp">
            <div className="bg-[#075E54] text-white px-5 py-4 flex items-center gap-3">
              <div className="relative">
                <img src={`${process.env.PUBLIC_URL || ""}/logo.jpg`} alt="" className="w-11 h-11 rounded-full object-cover bg-white"/>
                <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-[#25D366] ring-2 ring-[#075E54]"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{c.title || site.name}</div>
                {c.subtitle && <div className="text-[11px] opacity-80 truncate">{c.subtitle}</div>}
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close" className="p-1.5 rounded-full hover:bg-white/10"><X size={18}/></button>
            </div>
            <div className="px-4 py-4 max-h-[55vh] overflow-y-auto" style={{ backgroundColor: "#ECE5DD", backgroundImage: "radial-gradient(rgba(0,0,0,.035) 1px, transparent 1px)", backgroundSize: "14px 14px" }}>
              {c.greeting && (
                <div className="relative max-w-[85%] bg-white rounded-2xl rounded-tl-sm px-3.5 py-2.5 text-sm text-neutral-800 shadow-sm mb-4 whitespace-pre-line">
                  {c.greeting}
                </div>
              )}
              {messages.length > 0 && <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2 px-1">Choose a message</div>}
              <div className="space-y-2">
                {messages.map((m, i) => (
                  <button key={i} onClick={() => send(m.text || m.label)} data-testid={`wa-msg-${i}`}
                          className="w-full flex items-center gap-2 text-left bg-white hover:bg-[#DCF8C6] rounded-2xl px-3.5 py-2.5 text-sm text-neutral-800 shadow-sm transition-colors">
                    <span className="flex-1">{m.label || m.text}</span>
                    <ChevronRight size={15} className="text-[#075E54] shrink-0"/>
                  </button>
                ))}
              </div>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); if (text.trim()) { send(text.trim()); setText(""); } }} className="flex items-center gap-2 p-3 border-t bg-white">
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type your own message…" data-testid="wa-input"
                     className="flex-1 rounded-full bg-neutral-100 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#25D366]/40"/>
              <button type="submit" aria-label="Send on WhatsApp" className="w-10 h-10 rounded-full bg-[#25D366] hover:bg-[#1DA851] text-white flex items-center justify-center shrink-0"><Send size={16}/></button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {nudge && !open && (
          <motion.button initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} onClick={() => { setNudge(false); setOpen(true); }}
                         className="mb-3 bg-white rounded-2xl shadow-lg px-4 py-2.5 text-sm text-neutral-800 border border-neutral-100">
            Need help choosing a saree? 💬
          </motion.button>
        )}
      </AnimatePresence>

      <button onClick={() => { setOpen(!open); setNudge(false); }} aria-label={open ? "Close WhatsApp chat" : "Chat with us on WhatsApp"} data-testid="whatsapp-button"
              className="relative w-14 h-14 sm:w-[60px] sm:h-[60px] rounded-full bg-[#25D366] hover:bg-[#1DA851] text-white flex items-center justify-center shadow-[0_12px_30px_-6px_rgba(37,211,102,.7)] transition-transform hover:scale-105">
        {!open && <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30"/>}
        <AnimatePresence mode="wait" initial={false}>
          {open
            ? <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}><X size={26}/></motion.span>
            : <motion.span key="wa" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}><WhatsAppIcon size={30}/></motion.span>}
        </AnimatePresence>
      </button>
    </div>
  );
}
